import { useEffect, useState } from "react";

import { hasGlobalPrivacyControl, isOptedOut, setOptOut } from "@/analytics/optOut";

/**
 * The analytics opt-out control, rendered beneath the privacy policy.
 *
 * It cannot live *in* the policy text: that body is Markdown served from
 * the backend and rendered in react-markdown's no-raw-HTML mode, on
 * purpose, so a template edit can never become script execution on a
 * public page. The policy describes this control in prose; this is the
 * working version of it.
 *
 * Turning it on stores a preference this browser reads inline, in the
 * head, before the Tag Manager loader runs — so opting out means the
 * container is never fetched, not that it loads and is asked to stay
 * quiet.
 */

/**
 * Storage state is invisible to the prerender, so the first client
 * render has to match the static HTML or React reports a hydration
 * failure. Same shape as the Footer's build-year: render a stable
 * placeholder, then correct after mount.
 */
type Status = "pending" | "on" | "off" | "forced-off" | "unavailable";

function readStatus(): Status {
  if (hasGlobalPrivacyControl()) return "forced-off";
  try {
    window.localStorage.getItem("");
  } catch {
    return "unavailable";
  }
  return isOptedOut() ? "off" : "on";
}

export function AnalyticsOptOut() {
  const [status, setStatus] = useState<Status>("pending");

  useEffect(() => {
    setStatus(readStatus());
  }, []);

  function toggle(next: boolean) {
    if (!setOptOut(next)) {
      setStatus("unavailable");
      return;
    }
    // Setting the flag cannot unload a container that is already running
    // in this document. Reloading is what makes the choice true, and it
    // is what lets the copy above promise that it takes effect now.
    window.location.reload();
  }

  return (
    <div className="rounded-lg border border-line p-5">
      <h2 className="font-heading text-lg font-bold text-ink">Analytics opt-out</h2>
      <p className="mt-2">
        We use Google Analytics, loaded through Google Tag Manager, on our public pages only —
        never on the pages where you upload or work on a document. You can switch it off here.
        The preference is stored in this browser, so you would need to set it again on another
        browser or device. It sets no cookie.
      </p>

      {status === "pending" && <p className="mt-4 text-ink-hint">Checking this browser…</p>}

      {status === "forced-off" && (
        <p className="mt-4 text-ink-hint">
          Your browser sends a Global Privacy Control signal, so analytics is already off here.
          We honour that signal automatically and there is nothing for you to set.
        </p>
      )}

      {status === "unavailable" && (
        <p className="mt-4 text-ink-hint">
          This browser is blocking site storage, so we cannot save the preference — which also
          means the analytics loader cannot read one, and does not run. Analytics is off here.
        </p>
      )}

      {(status === "on" || status === "off") && (
        <button
          type="button"
          onClick={() => toggle(status === "on")}
          className="btn-indigo mt-4 inline-flex items-center px-5 py-3"
        >
          {status === "on" ? "Turn analytics off in this browser" : "Turn analytics back on"}
        </button>
      )}

      {status === "off" && (
        <p className="mt-3 text-ink-hint">Analytics is currently off in this browser.</p>
      )}
    </div>
  );
}
